import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Toolbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import firebaseService from '../firebase/firebaseService';
import { auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { TIERS, TIER_LIMITS } from '../models/UserTier';

const AdminPage = () => {
  const [tabValue, setTabValue] = useState(0); // 0 = Allowed Emails, 1 = User Tiers
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [users, setUsers] = useState([]);
  const [mergedData, setMergedData] = useState([]); // Emails merged with user tier data
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [tierFilter, setTierFilter] = useState('all'); // 'all', 'free', 'premium'
  const [sortBy, setSortBy] = useState('email'); // 'email', 'tier', 'addedAt'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [stats, setStats] = useState({
    totalUsers: 0,
    registeredUsers: 0,
    freeUsers: 0,
    premiumUsers: 0,
    notSignedIn: 0,
    activeToday: 0
  });
  const [selectedEmails, setSelectedEmails] = useState([]); // For multi-select
  const [bulkTier, setBulkTier] = useState(''); // For bulk tier change
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          // Not logged in, redirect to login
          navigate('/');
          return;
        }

        const isAdmin = await firebaseService.isUserAdmin(user.uid);
        if (!isAdmin) {
          // Not an admin, redirect to home
          navigate('/');
          setError('You do not have permission to access this page');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  // Load allowed emails
  useEffect(() => {
    console.log('🔧 Setting up allowedEmails listener...');
    const allowedEmailsRef = collection(db, 'allowedEmails');
    const q = query(allowedEmailsRef, orderBy('addedAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const emails = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('📧 AllowedEmails collection updated:', emails.length, 'emails');
      setAllowedEmails(emails);
      setLoading(false);
    }, (error) => {
      console.error('Error loading allowed emails:', error);
      setError('Error loading allowed emails');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load all users for tier data with real-time updates
  useEffect(() => {
    console.log('🔧 Setting up users listener...');
    // Set up real-time listener for users collection
    const usersRef = collection(db, 'users');
    const unsubscribe = onSnapshot(usersRef, (snapshot) => {
      const updatedUsers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('👥 Users collection updated:', updatedUsers.length, 'users');
      console.log('📋 User emails:', updatedUsers.map(u => u.email).join(', '));
      setUsers(updatedUsers);
      setLoading(false);
    }, (error) => {
      console.error('❌ Error listening to users:', error);
      setLoading(false);
    });

    return () => {
      console.log('🔌 Unsubscribing from users listener');
      unsubscribe();
    };
  }, []);

  // Merge allowed emails with user tier data
  useEffect(() => {
    console.log('🔄 Merging data - Emails:', allowedEmails.length, 'Users:', users.length);

    const merged = allowedEmails.map(emailEntry => {
      // Find matching user by email
      const user = users.find(u => u.email === emailEntry.id);

      if (user) {
        console.log('✅ Found user for', emailEntry.id, '- lastActive:', user.lastActive);
      } else {
        console.log('❌ No user found for', emailEntry.id);
      }

      return {
        email: emailEntry.id,
        addedAt: emailEntry.addedAt,
        addedBy: emailEntry.addedBy,
        tier: user?.tier || TIERS.FREE,
        userId: user?.id || null,
        displayName: user?.displayName || null,
        matchPullsToday: user?.matchPullsToday || 0,
        lastActive: user?.lastActive || null,
        createdAt: user?.createdAt || null
      };
    });

    console.log('📦 Merged data:', merged.length, 'entries');
    setMergedData(merged);

    // Calculate statistics
    const totalUsers = merged.length;
    const registeredUsers = merged.filter(u => u.userId).length;
    const notSignedIn = totalUsers - registeredUsers;
    const freeUsers = merged.filter(u => u.userId && u.tier === TIERS.FREE).length;
    const premiumUsers = merged.filter(u => u.userId && u.tier === TIERS.PREMIUM).length;

    // Active today = users with lastActive timestamp within last 24 hours
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    const activeToday = merged.filter(u => {
      if (!u.lastActive) return false;
      const lastActiveTime = u.lastActive.seconds ? u.lastActive.seconds * 1000 : u.lastActive;
      return lastActiveTime > oneDayAgo;
    }).length;

    setStats({
      totalUsers,
      registeredUsers,
      freeUsers,
      premiumUsers,
      notSignedIn,
      activeToday
    });
  }, [allowedEmails, users]);

  const handleAddEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail) {
      setError('Please enter an email address');
      return;
    }
    
    try {
      setLoading(true);
      await firebaseService.addAllowedEmail(newEmail);
      setNewEmail('');
      setSuccess(`${newEmail} added to allowed list`);
    } catch (error) {
      console.error('Error adding email:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async () => {
    if (!emailToDelete) return;
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'allowedEmails', emailToDelete));
      setSuccess(`Email removed from allowed list`);
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
    } catch (error) {
      console.error('Error deleting email:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (email) => {
    setEmailToDelete(email);
    setDeleteDialogOpen(true);
  };

  // Handle tier update from Allowed Emails tab
  const handleTierChange = async (email, userId, newTier) => {
    try {
      if (userId) {
        // User has signed in - update in users collection
        await firebaseService.updateUserTier(userId, newTier);
      } else {
        // User hasn't signed in yet - update allowedEmails collection
        await firebaseService.updateAllowedEmailTier(email, newTier);
      }

      setSuccess(`Tier updated to ${TIER_LIMITS[newTier].label} for ${email}`);

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, tier: newTier } : user
      ));
    } catch (error) {
      console.error('Error updating tier:', error);
      setError(error.message);
    }
  };

  // Multi-select handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allEmails = getFilteredAndSortedData().map(item => item.email);
      setSelectedEmails(allEmails);
    } else {
      setSelectedEmails([]);
    }
  };

  const handleSelectOne = (email) => {
    setSelectedEmails(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleBulkTierChange = async () => {
    if (!bulkTier) {
      setError('Please select a tier');
      return;
    }

    if (selectedEmails.length === 0) {
      setError('Please select at least one user');
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;

      for (const email of selectedEmails) {
        const item = mergedData.find(i => i.email === email);
        if (item) {
          if (item.userId) {
            // User has signed in - update users collection
            await firebaseService.updateUserTier(item.userId, bulkTier);
          } else {
            // User hasn't signed in - update allowedEmails collection
            await firebaseService.updateAllowedEmailTier(email, bulkTier);
          }
          successCount++;
        }
      }

      setSuccess(`Updated ${successCount} user(s) to ${TIER_LIMITS[bulkTier].label}`);
      setSelectedEmails([]);
      setBulkTier('');
    } catch (error) {
      console.error('Error updating tiers:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get filtered and sorted data
  const getFilteredAndSortedData = () => {
    let filtered = [...mergedData];

    // Apply tier filter
    if (tierFilter !== 'all') {
      filtered = filtered.filter(item => item.tier === tierFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      if (sortBy === 'email') {
        compareValue = a.email.localeCompare(b.email);
      } else if (sortBy === 'tier') {
        const tierOrder = { [TIERS.PREMIUM]: 2, [TIERS.FREE]: 1 };
        compareValue = (tierOrder[a.tier] || 0) - (tierOrder[b.tier] || 0);
      } else if (sortBy === 'status') {
        // Sort by signed in status: signed in first, then not signed in
        compareValue = (b.userId ? 1 : 0) - (a.userId ? 1 : 0);
      } else if (sortBy === 'lastActive') {
        const aTime = a.lastActive?.seconds || 0;
        const bTime = b.lastActive?.seconds || 0;
        compareValue = bTime - aTime; // Most recent first by default
      } else if (sortBy === 'addedAt') {
        const aTime = a.addedAt?.seconds || 0;
        const bTime = b.addedAt?.seconds || 0;
        compareValue = aTime - bTime;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  };

  // Handle sort change
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Get tier icon
  const getTierIcon = (tier) => {
    return tier === TIERS.PREMIUM ?
      <WorkspacePremiumIcon sx={{ color: TIER_LIMITS[TIERS.PREMIUM].color, fontSize: 20 }} /> :
      <FreeBreakfastIcon sx={{ color: TIER_LIMITS[TIERS.FREE].color, fontSize: 20 }} />;
  };

  // Format last active timestamp
  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never';

    const timestamp = lastActive.seconds ? lastActive.seconds * 1000 : lastActive;
    const now = Date.now();
    const diff = now - timestamp;

    // Less than 1 minute
    if (diff < 60000) return 'Just now';

    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes}m ago`;
    }

    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }

    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}d ago`;
    }

    // Format as date
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Get activity status color
  const getActivityColor = (lastActive) => {
    if (!lastActive) return 'text.secondary';

    const timestamp = lastActive.seconds ? lastActive.seconds * 1000 : lastActive;
    const diff = Date.now() - timestamp;

    if (diff < 3600000) return 'success.main'; // < 1 hour = green
    if (diff < 86400000) return 'warning.main'; // < 24 hours = orange
    return 'text.secondary'; // older = gray
  };

  // Load all users for tier management
  useEffect(() => {
    if (tabValue !== 1) return; // Only load when on User Tiers tab

    const loadUsers = async () => {
      try {
        setLoading(true);
        const allUsers = await firebaseService.getAllUsers();
        setUsers(allUsers);
      } catch (error) {
        console.error('Error loading users:', error);
        setError('Error loading users');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [tabValue]);

  const handleUpdateTier = async (userId, newTier) => {
    try {
      await firebaseService.updateUserTier(userId, newTier);
      setSuccess(`User tier updated to ${TIER_LIMITS[newTier].label}`);

      // Update local state
      setUsers(users.map(user =>
        user.id === userId ? { ...user, tier: newTier } : user
      ));
    } catch (error) {
      console.error('Error updating tier:', error);
      setError(error.message);
    }
  };

  const getTierColor = (tier) => {
    return tier === TIERS.PREMIUM ? 'warning' : 'default';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Panel
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Allowed Emails" />
          <Tab label="User Tiers" />
        </Tabs>
      </Paper>

      {/* TAB 0: ALLOWED EMAILS */}
      {tabValue === 0 && (
        <>
          {/* Statistics Dashboard */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(25, 118, 210, 0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">{stats.totalUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Emails</Typography>
                    </Box>
                    <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="success.main">{stats.registeredUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">Registered Users</Typography>
                    </Box>
                    <PersonAddIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.premiumUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">Premium Users</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.freeUsers} Free
                      </Typography>
                    </Box>
                    <WorkspacePremiumIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" sx={{ color: '#9c27b0' }}>{stats.activeToday}</Typography>
                      <Typography variant="body2" color="text.secondary">Active Today</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.notSignedIn} not signed in
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, color: '#9c27b0', opacity: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mb: 4, bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Admin Access Notice:</strong> Manage allowed emails and their tier levels.
              Users must sign in before you can assign their tier. Stats update in real-time.
            </Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Add New Allowed Email
            </Typography>
            <Box component="form" onSubmit={handleAddEmail} sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email to allow"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={loading}
              >
                Add
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Allowed Emails & Tiers
              </Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Tier</InputLabel>
                <Select
                  value={tierFilter}
                  label="Filter by Tier"
                  onChange={(e) => setTierFilter(e.target.value)}
                >
                  <MenuItem value="all">All Tiers</MenuItem>
                  <MenuItem value={TIERS.FREE}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FreeBreakfastIcon sx={{ fontSize: 16 }} />
                      Free
                    </Box>
                  </MenuItem>
                  <MenuItem value={TIERS.PREMIUM}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkspacePremiumIcon sx={{ fontSize: 16 }} />
                      Premium
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Bulk Actions Toolbar */}
            {selectedEmails.length > 0 && (
              <Toolbar sx={{ bgcolor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1, mb: 2, gap: 2 }}>
                <Typography variant="subtitle1" sx={{ flex: '1 1 100%' }}>
                  {selectedEmails.length} selected
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Change Tier To</InputLabel>
                  <Select
                    value={bulkTier}
                    label="Change Tier To"
                    onChange={(e) => setBulkTier(e.target.value)}
                  >
                    <MenuItem value={TIERS.FREE}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FreeBreakfastIcon sx={{ fontSize: 16 }} />
                        Free
                      </Box>
                    </MenuItem>
                    <MenuItem value={TIERS.PREMIUM}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WorkspacePremiumIcon sx={{ fontSize: 16 }} />
                        Premium
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBulkTierChange}
                  disabled={!bulkTier}
                >
                  Apply to Selected
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedEmails([])}
                >
                  Clear Selection
                </Button>
              </Toolbar>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : mergedData.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No emails in the allowlist yet
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedEmails.length > 0 && selectedEmails.length < getFilteredAndSortedData().length}
                          checked={getFilteredAndSortedData().length > 0 && selectedEmails.length === getFilteredAndSortedData().length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'email'}
                          direction={sortBy === 'email' ? sortOrder : 'asc'}
                          onClick={() => handleSort('email')}
                        >
                          Email
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'tier'}
                          direction={sortBy === 'tier' ? sortOrder : 'asc'}
                          onClick={() => handleSort('tier')}
                        >
                          Tier
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortOrder : 'asc'}
                          onClick={() => handleSort('status')}
                        >
                          User Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'lastActive'}
                          direction={sortBy === 'lastActive' ? sortOrder : 'asc'}
                          onClick={() => handleSort('lastActive')}
                        >
                          Last Active
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Tier Management</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredAndSortedData().map((item) => (
                      <TableRow
                        key={item.email}
                        hover
                        selected={selectedEmails.includes(item.email)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedEmails.includes(item.email)}
                            onChange={() => handleSelectOne(item.email)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {item.email}
                            </Typography>
                            {item.displayName && (
                              <Typography variant="caption" color="text.secondary">
                                {item.displayName}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getTierIcon(item.tier)}
                            label={TIER_LIMITS[item.tier].label}
                            size="small"
                            sx={{
                              bgcolor: item.tier === TIERS.PREMIUM ? 'rgba(255, 167, 38, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                              color: TIER_LIMITS[item.tier].color,
                              fontWeight: 'medium'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {item.userId ? (
                            <Chip label="Signed In" size="small" color="success" />
                          ) : (
                            <Chip label="Not Signed In" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: getActivityColor(item.lastActive) }}
                          >
                            {formatLastActive(item.lastActive)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={item.tier}
                              onChange={(e) => handleTierChange(item.email, item.userId, e.target.value)}
                            >
                              <MenuItem value={TIERS.FREE}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FreeBreakfastIcon sx={{ fontSize: 16 }} />
                                  {TIER_LIMITS[TIERS.FREE].label}
                                </Box>
                              </MenuItem>
                              <MenuItem value={TIERS.PREMIUM}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <WorkspacePremiumIcon sx={{ fontSize: 16 }} />
                                  {TIER_LIMITS[TIERS.PREMIUM].label}
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            edge="end"
                            onClick={() => openDeleteDialog(item.email)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* TAB 1: USER TIERS */}
      {tabValue === 1 && (
        <>
          <Paper sx={{ p: 3, mb: 4, bgcolor: 'rgba(255, 152, 0, 0.08)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1">
                <strong>Tier Management:</strong> Control user access levels. Free tier has limited features,
                Premium tier has full access.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                onClick={async () => {
                  if (window.confirm('Upgrade ALL existing users to Premium tier?')) {
                    try {
                      setLoading(true);
                      const result = await firebaseService.upgradeAllUsersToPremium();
                      setSuccess(`✅ Upgraded ${result.upgraded} users to Premium (${result.alreadyPremium} already Premium)`);
                      // Reload users
                      const allUsers = await firebaseService.getAllUsers();
                      setUsers(allUsers);
                    } catch (error) {
                      setError(`Failed to upgrade users: ${error.message}`);
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
              >
                Upgrade All to Premium
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Registered Users
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No users registered yet
              </Typography>
            ) : (
              <List>
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography>{user.displayName || user.email}</Typography>
                            <Chip
                              label={TIER_LIMITS[user.tier || TIERS.FREE].label}
                              color={getTierColor(user.tier || TIERS.FREE)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {user.email}
                            </Typography>
                            {user.matchPullsToday > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                 • Match pulls today: {user.matchPullsToday}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Tier</InputLabel>
                        <Select
                          value={user.tier || TIERS.FREE}
                          label="Tier"
                          onChange={(e) => handleUpdateTier(user.id, e.target.value)}
                        >
                          <MenuItem value={TIERS.FREE}>
                            {TIER_LIMITS[TIERS.FREE].label}
                          </MenuItem>
                          <MenuItem value={TIERS.PREMIUM}>
                            {TIER_LIMITS[TIERS.PREMIUM].label}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this email from the allowed list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEmail} color="error">Remove</Button>
        </DialogActions>
      </Dialog>
      
      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPage; 